import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Filter, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../api/client';

const SearchBar = ({ isCompact = false }) => {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions when query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await projectsAPI.search(query);
        const limitedResults = results.slice(0, 5); // Show only first 5 suggestions
        setSuggestions(limitedResults);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(query.trim())}`);
      setShowSuggestions(false);
      if (isCompact) {
        setIsExpanded(false);
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.title);
    setShowSuggestions(false);
    navigate(`/buscar?q=${encodeURIComponent(suggestion.title)}`);
    if (isCompact) {
      setIsExpanded(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const getCategoryBadgeColor = (categoryType) => {
    switch (categoryType) {
      case 'digital':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'printed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'sites':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCategoryName = (categoryType) => {
    switch (categoryType) {
      case 'digital':
        return 'Digital';
      case 'printed':
        return 'Impreso';
      case 'sites':
        return 'Sitio';
      default:
        return 'Recurso';
    }
  };

  if (isCompact && !isExpanded) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsExpanded(true)}
        className="hover:bg-gray-100"
      >
        <Search className="w-4 h-4" />
      </Button>
    );
  }

  return (
    <div ref={searchRef} className="relative">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Buscar proyectos, autores, temas..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={`${
              isCompact ? 'w-64' : 'w-full'
            } pl-10 pr-20 focus:ring-emerald-500 focus:border-emerald-500`}
            autoComplete="off"
          />
          
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {query && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearSearch}
                className="h-6 w-6 p-0 hover:bg-gray-100"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
            
            {isCompact && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="h-6 w-6 p-0 hover:bg-gray-100"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
            
            <Button
              type="submit"
              size="sm"
              className="h-6 bg-emerald-600 hover:bg-emerald-700 text-white px-2"
              disabled={!query.trim()}
            >
              <Search className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (suggestions.length > 0 || isLoading) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-gray-500">
              Buscando...
            </div>
          ) : (
            <>
              <div className="p-2 border-b border-gray-100">
                <p className="text-xs text-gray-500 font-medium">
                  Sugerencias ({suggestions.length})
                </p>
              </div>
              
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full p-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
                >
                  <div className="flex items-start space-x-3">
                    <Search className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {suggestion.title}
                        </p>
                        <Badge className={`text-xs ${getCategoryBadgeColor(suggestion.categoryType)}`}>
                          {getCategoryName(suggestion.categoryType)}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {suggestion.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Por {suggestion.author}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
              
              {query.trim() && (
                <button
                  onClick={handleSubmit}
                  className="w-full p-3 text-left hover:bg-emerald-50 transition-colors border-t border-gray-100 text-emerald-600"
                >
                  <div className="flex items-center space-x-3">
                    <Search className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Ver todos los resultados para "{query}"
                    </span>
                  </div>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;